import React, { useCallback, useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Save, Eye, Upload, TestTube2, RotateCcw, Images, Trash2 } from "lucide-react";
import api from "../../lib/api";
import RichTextEditor from "../../components/RichTextEditor";

export default function WelcomePack() {
  const [tpl, setTpl] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewName, setPreviewName] = useState("Student");
  const [previewSession, setPreviewSession] = useState("tue_am");
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/welcome-template");
    setTpl(data);
    const imgs = await api.get("/admin/welcome-images");
    setImages(imgs.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/welcome-template", tpl);
      toast.success("Welcome template saved");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const doPreview = async () => {
    const { data } = await api.post("/admin/welcome-template/preview", {
      name: previewName, session_key: previewSession,
    });
    setPreview(data);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/welcome-template/test-send", { name: previewName, session_key: previewSession });
      toast.success(`Test welcome letter sent to ${data.queued_to || data.to} ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  const restoreDefaults = async () => {
    if (!confirm("Restore the default welcome letter subject and body? Your curriculum attachment will be kept.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/welcome-template/restore-defaults");
      setTpl(data);
      toast.success("Welcome letter restored to defaults");
    } catch { toast.error("Restore failed"); } finally { setBusy(false); }
  };

  const uploadImages = async (files) => {
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`${f.name}: only JPG/PNG/WebP accepted`);
        continue;
      }
      const dataUrl = await fileToB64(f);
      const base64 = dataUrl.split(",")[1];
      try { await api.post("/admin/welcome-images", { filename: f.name, mime_type: f.type, image_base64: base64 }); }
      catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || "Upload failed"}`); }
    }
    toast.success("Images uploaded — they'll appear in the next welcome letter send");
    await load();
  };

  const deleteImage = async (id) => {
    if (!confirm("Remove this image from the welcome letter?")) return;
    await api.delete(`/admin/welcome-images/${id}`);
    await load();
  };

  /** Remove every image tile whose thumbnail returned 404 (data corrupt or
   * truncated on upload). Called from the "Clean up broken images" button
   * that only shows if we've detected at least one broken tile this session. */
  const [brokenIds, setBrokenIds] = useState(new Set());
  const markBroken = useCallback((id) => {
    setBrokenIds((s) => (s.has(id) ? s : new Set([...s, id])));
  }, []);
  const deleteBroken = async () => {
    if (brokenIds.size === 0) return;
    if (!confirm(`Delete ${brokenIds.size} broken/corrupt image${brokenIds.size === 1 ? "" : "s"}? These couldn't be rendered — usually because their upload didn't finish. Safe to remove and re-upload.`)) return;
    let ok = 0;
    for (const id of brokenIds) {
      try { await api.delete(`/admin/welcome-images/${id}`); ok++; }
      catch { /* keep going */ }
    }
    toast.success(`Removed ${ok} broken image${ok === 1 ? "" : "s"}. Re-upload them when you're ready.`);
    setBrokenIds(new Set());
    await load();
  };

  const uploadCurriculum = async (file) => {
    if (!file) return;
    const b64 = await fileToB64(file);
    setTpl({ ...tpl, curriculum_filename: file.name, curriculum_base64: b64.split(",")[1] });
    toast.success("Curriculum attached (remember to save)");
  };

  if (!tpl) return <div className="text-[#6E6074]">Loading…</div>;

  return (
    <div data-testid="admin-welcome-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">automated welcome pack</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Welcome letter</h1>
          <p className="text-sm text-[#6E6074] mt-1">Use merge tags: <code>{"{{name}}"}</code>, <code>{"{{session}}"}</code>, <code>{"{{session_time}}"}</code>, <code>{"{{term}}"}</code>, <code>{"{{price}}"}</code>.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={restoreDefaults} disabled={busy} className="sac-btn-secondary" data-testid="welcome-restore-btn"><RotateCcw size={16} /> Restore defaults</button>
          <button onClick={save} disabled={busy} className="sac-btn-primary" data-testid="welcome-save-btn"><Save size={16} /> Save template</button>
        </div>
      </div>

      {!tpl.curriculum_base64 && (
        <div className="admin-surface p-4 mb-5 bg-[#E8C37A]/15 border-[#E8C37A]/50">
          <div className="text-sm text-[#8F6B1F]">
            <strong>Curriculum PDF not attached to welcome letter yet.</strong> Upload it below — once uploaded it will automatically be attached to every welcome letter, reminder email and bulk email send. If you already uploaded the curriculum in <em>Admin → Curriculum</em>, re-upload it there and it will now mirror here automatically.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="admin-surface p-5 space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">Subject line</label>
            <input value={tpl.subject || ""} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="welcome-subject" />
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Term name</label>
            <input value={tpl.term || ""} onChange={(e) => setTpl({ ...tpl, term: e.target.value })} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="welcome-term" />
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Email body</label>
            <p className="text-xs text-[#6E6074] mt-0.5 leading-snug">Use the toolbar for headings, bold/italic, lists, links and inline images. Use <code>{`{{name}}`}</code>, <code>{`{{session}}`}</code>, <code>{`{{session_time}}`}</code>, <code>{`{{term}}`}</code>, <code>{`{{price}}`}</code> as merge tags — they're swapped per recipient.</p>
            <div className="mt-2">
              <RichTextEditor
                value={tpl.body_html || ""}
                onChange={(html) => setTpl({ ...tpl, body_html: html })}
                placeholder="Write the welcome letter…"
                testid="welcome-body"
                minHeight={420}
              />
            </div>
          </div>
          <div className="pt-4 border-t border-[#E5E0D8]">
            <label className="text-sm text-[#5D2C8F]">Curriculum PDF for this term</label>
            <div className="mt-2 flex items-center gap-3">
              <label className="sac-btn-secondary cursor-pointer !py-2 !px-4 text-sm">
                <Upload size={14} /> Upload PDF
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadCurriculum(e.target.files?.[0])} data-testid="welcome-curriculum-upload" />
              </label>
              {tpl.curriculum_filename && <span className="text-sm text-[#5D2C8F]">{tpl.curriculum_filename}</span>}
            </div>
          </div>
        </div>

        <div className="admin-surface p-5">
          <h3 className="font-display text-xl text-[#5D2C8F] mb-3">Preview</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input value={previewName} onChange={(e) => setPreviewName(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" placeholder="Student name" data-testid="welcome-preview-name" />
            <select value={previewSession} onChange={(e) => setPreviewSession(e.target.value)} className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="welcome-preview-session">
              <option value="tue_am">Tuesday AM</option>
              <option value="tue_pm">Tuesday PM</option>
              <option value="fri_eve">Friday Evening</option>
            </select>
          </div>
          <button onClick={doPreview} className="sac-btn-primary !py-2 !px-4 text-sm mr-2" data-testid="welcome-preview-btn"><Eye size={14} /> Render preview</button>
          <button onClick={sendTest} disabled={busy} className="sac-btn-secondary !py-2 !px-4 text-sm" data-testid="welcome-test-btn"><TestTube2 size={14} /> Send test to my inbox</button>
          {preview && (
            <div className="mt-4">
              <div className="text-xs text-[#6E6074] mb-1">Subject:</div>
              <div className="font-medium text-[#5D2C8F] mb-3">{preview.subject}</div>
              <div className="border border-[#E5E0D8] rounded-xl p-1 bg-white">
                <iframe title="preview" srcDoc={preview.html} className="w-full h-[520px] rounded-lg" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Welcome-letter images panel */}
      <div className="admin-surface p-5 mt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><Images size={18} /> Welcome letter images</h3>
            <p className="text-xs text-[#6E6074] mt-1">Up to 6 of these are inserted into every welcome letter send, arranged as a 3-column gallery just above the sign-off. Drop in images of student work or class life.</p>
          </div>
          <div className="flex items-center gap-2">
            {brokenIds.size > 0 && (
              <button onClick={deleteBroken} className="text-xs text-[#A685BD] hover:text-[#8e6da9] underline underline-offset-2" data-testid="welcome-images-clean-broken">
                Clean up {brokenIds.size} broken image{brokenIds.size === 1 ? "" : "s"}
              </button>
            )}
            <label className="sac-btn-primary cursor-pointer text-sm" data-testid="welcome-images-upload-label">
              <Upload size={14} /> Upload images
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => uploadImages(e.target.files)} data-testid="welcome-images-upload" />
            </label>
          </div>
        </div>
        {images.length === 0 ? (
          <div className="text-sm text-[#6E6074] bg-[#FAF6EE] rounded-lg p-6 text-center">No welcome-letter images yet. Upload some to see them appear in the email.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {images.map((img, i) => {
              const broken = brokenIds.has(img.id);
              return (
                <div key={img.id} className={`relative group ${broken ? "opacity-70" : ""}`} data-testid={`welcome-image-${img.id}`}>
                  {broken ? (
                    <div className="w-full aspect-square rounded-lg border border-dashed border-[#A685BD] bg-[#F4EDDD] flex flex-col items-center justify-center p-2 text-center" data-testid={`welcome-image-broken-${img.id}`}>
                      <span className="text-[10px] uppercase tracking-widest text-[#A685BD] font-semibold">Broken</span>
                      <span className="text-[10px] text-[#6E6074] mt-1 leading-tight">Upload didn&apos;t complete — please re-upload</span>
                    </div>
                  ) : (
                    <img
                      src={`/api/public/welcome-images/${img.id}.jpg?size=300`}
                      alt={img.filename || ""}
                      onError={() => markBroken(img.id)}
                      className="w-full aspect-square object-cover rounded-lg border border-[#E5E0D8]"
                    />
                  )}
                  {i < 6 && !broken && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-[#5D2C8F] text-white">in email</span>}
                  {i >= 6 && !broken && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-white text-[#6E6074]">extra</span>}
                  <button onClick={() => deleteImage(img.id)} className="absolute top-1 right-1 p-1 rounded bg-white/90 text-[#A685BD] opacity-0 group-hover:opacity-100 transition" data-testid={`welcome-image-del-${img.id}`}><Trash2 size={12} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Per-session curriculum PDFs */}
      <CurriculumVariantsPanel />
    </div>
  );
}

function fileToB64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/**
 * Manage multiple curriculum PDFs, each either per-session (tue_am / tue_pm /
 * fri_eve) or marked as the default fall-through. The welcome-pack email
 * auto-picks the right one based on each student's session.
 */
function CurriculumVariantsPanel() {
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSession, setNewSession] = useState("");
  const [newIsDefault, setNewIsDefault] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/curriculum-variants"); setRows(data || []); }
    catch { /* non-fatal — user sees empty list */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!newLabel.trim()) { toast.error("Give this curriculum a label first"); return; }
    setUploading(true);
    try {
      const dataUrl = await fileToB64(file);
      const pdf_base64 = dataUrl.split(",", 2)[1] || "";
      await api.post("/admin/curriculum-variants", {
        label: newLabel.trim(),
        session_key: newSession || null,
        is_default: newIsDefault,
        pdf_base64,
        filename: file.name,
      });
      setNewLabel(""); setNewSession(""); setNewIsDefault(false);
      toast.success("Curriculum uploaded ✓");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const patch = async (id, body) => {
    try { await api.patch(`/admin/curriculum-variants/${id}`, body); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this curriculum PDF?")) return;
    await api.delete(`/admin/curriculum-variants/${id}`);
    await load();
  };

  const sessionLabel = (k) => ({ tue_am: "Tuesday AM", tue_pm: "Tuesday PM", fri_eve: "Friday Evening" })[k] || "Default";

  return (
    <div className="admin-surface p-5 mt-5" data-testid="curriculum-variants-panel">
      <div className="mb-3">
        <h3 className="font-display text-xl text-[#5D2C8F] flex items-center gap-2"><Upload size={18} /> Curriculum PDFs</h3>
        <p className="text-xs text-[#6E6074] leading-snug mt-1 max-w-2xl">
          Upload different PDFs for each session. When a welcome pack or bulk email goes out, the right curriculum is attached automatically based on the student's session. If a session doesn't have its own PDF, the one marked <strong>Default</strong> is used as a fall-back.
        </p>
      </div>

      {/* Upload new variant */}
      <div className="grid md:grid-cols-4 gap-3 items-end bg-[#FAF6EE] p-4 rounded-lg border border-[#E5E0D8]">
        <div>
          <label className="text-xs text-[#5D2C8F] block mb-1">Label</label>
          <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Summer 2026 – morning" className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm" data-testid="curriculum-variant-label" />
        </div>
        <div>
          <label className="text-xs text-[#5D2C8F] block mb-1">Applies to session</label>
          <select value={newSession} onChange={(e) => setNewSession(e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-sm" data-testid="curriculum-variant-session">
            <option value="">— Any / default —</option>
            <option value="tue_am">Tuesday AM</option>
            <option value="tue_pm">Tuesday PM</option>
            <option value="fri_eve">Friday Evening</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-xs text-[#5D2C8F] h-10" data-testid="curriculum-variant-default-row">
          <input type="checkbox" checked={newIsDefault} onChange={(e) => setNewIsDefault(e.target.checked)} className="w-4 h-4 accent-[#5D2C8F]" data-testid="curriculum-variant-is-default" />
          Mark as default (used when no session match)
        </label>
        <label className="sac-btn-primary !py-2 !px-3 text-sm cursor-pointer justify-center" data-testid="curriculum-variant-upload-btn">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="curriculum-variant-upload-input" disabled={uploading} />
        </label>
      </div>

      {/* Existing variants */}
      {rows.length === 0 ? (
        <div className="text-xs text-[#6E6074] bg-[#FAF6EE] rounded-lg p-5 text-center mt-4">No custom curriculums yet — the legacy single PDF (if any) is used until you add one.</div>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] text-[#6E6074] uppercase tracking-widest">
              <tr><th className="py-2 pr-3">Label</th><th className="py-2 pr-3">Session</th><th className="py-2 pr-3">Default?</th><th className="py-2 pr-3">File</th><th className="py-2 pr-3 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-[#E5E0D8]" data-testid={`curriculum-variant-row-${v.id}`}>
                  <td className="py-2 pr-3 text-[#5D2C8F] font-medium">{v.label}</td>
                  <td className="py-2 pr-3">
                    <select value={v.session_key || ""} onChange={(e) => patch(v.id, { session_key: e.target.value || null })} className="bg-white border border-[#E5E0D8] rounded px-2 py-1 text-xs" data-testid={`curriculum-variant-session-${v.id}`}>
                      <option value="">— Any —</option>
                      <option value="tue_am">Tuesday AM</option>
                      <option value="tue_pm">Tuesday PM</option>
                      <option value="fri_eve">Friday Evening</option>
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <label className="inline-flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={!!v.is_default} onChange={(e) => patch(v.id, { is_default: e.target.checked })} data-testid={`curriculum-variant-default-${v.id}`} />
                      {v.is_default ? <span className="text-[#5D2C8F]">Default</span> : <span className="text-[#6E6074]">—</span>}
                    </label>
                  </td>
                  <td className="py-2 pr-3 text-xs text-[#6E6074]">{v.filename} <span className="text-[10px] opacity-60">({Math.round((v.size_bytes || 0) / 1024)} KB)</span></td>
                  <td className="py-2 pr-3 text-right">
                    <button onClick={() => remove(v.id)} className="text-[#A685BD] hover:text-[#5D2C8F]" data-testid={`curriculum-variant-delete-${v.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-[#6E6074] mt-3">Tip: leave all Session fields as "Any" and tick one as the Default if you just want a single curriculum for everyone. Otherwise assign a specific session — that's what gets attached for students in that slot.</p>
        </div>
      )}
    </div>
  );
}
